<template>
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
    <div class="bg-bkk-25 flex flex-col rounded-lg p-2">
      <div
        v-if="
          challengeType ===
          CompetitiveChallengeType.COMPETITIVE_CHALLENGE_TYPE_STEPS
        "
        class="mb-4"
      >
        <WalkIcon class="w-6 h-6" />
      </div>
      <div v-else class="mb-4">
        <BikeIcon class="w-6 h-6" />
      </div>
      <div>
        {{ formatProgressValue(teamTotalProgress) }}
      </div>
      <div
        v-if="
          challengeType ===
          CompetitiveChallengeType.COMPETITIVE_CHALLENGE_TYPE_STEPS
        "
      >
        {{
          t("comp.challenge.started.tab.team.progress.label", {
            s: t("challenges.tab.filter.steps"),
          })
        }}
      </div>
      <div v-else>
        {{
          t("comp.challenge.started.tab.team.progress.label", {
            s: t("bicycle.semantics"),
          })
        }}
      </div>
    </div>
    <div class="bg-bkk-25 flex flex-col rounded-lg p-2">
      <div class="mb-4"><CO2Icon /></div>
      <div>
        {{ formatCompetitiveChallengeCo2(teamTotalCo2Saving) }}
      </div>
      <div>
        {{ t("comp.challenge.started.tab.ranking.card.co2.label") }}
      </div>
    </div>
  </div>
  <HeadlineElement>
    {{ t("comp.challenge.started.tab.team.list.label") }}
  </HeadlineElement>
  <div v-for="member in sortedTeam" :key="member.id">
    <TeamMemberElement
      :team-member="member"
      :team-challenge-type="challengeType"
      :ranking-sort-type="rankingSortType"
      :is-current-user-captain="isCurrentUserCaptain"
      :team-name="teamName"
      @kick-member="onKickMember"
    />
  </div>
  <KickTeamMemberModal
    :open="kickModalOpen"
    :team-id="teamId"
    :member-id="kickTargetMemberId"
    :member-name="kickTargetMemberName"
    :team-name="teamName"
    @close="kickModalOpen = false"
    @member-kicked="onMemberKicked"
  />
</template>
<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { CompetitiveChallengeType } from "../../../enum/v2/competitive-challenge-type";
import WalkIcon from "../icon/WalkIcon.vue";
import BikeIcon from "../icon/BikeIcon.vue";
import CO2Icon from "../icon/CO2Icon.vue";
import HeadlineElement from "../layout/HeadlineElement.vue";
import TeamMemberElement from "./TeamMemberElement.vue";
import KickTeamMemberModal from "./KickTeamMemberModal.vue";
import { CompetitiveChallengeTeamMemberDto } from "../../../dto/competitive-challenge-team-member.dto";
import {
  formatCompetitiveChallengeCo2,
  formatCompetitiveChallengeProgress,
} from "../../../plugins/NumberFormat";
const { t } = useI18n();

const props = defineProps({
  team: {
    type: Array as PropType<CompetitiveChallengeTeamMemberDto[]>,
    required: true,
  },
  challengeType: {
    type: String as PropType<CompetitiveChallengeType>,
    required: true,
  },
  teamId: {
    type: Number,
    required: true,
  },
  teamName: {
    type: String,
    required: true,
  },
  teamTotalProgress: {
    type: Number,
    required: true,
  },
  teamTotalCo2Saving: {
    type: Number,
    required: true,
  },
  rankingSortType: {
    type: String as PropType<RankingSortType>,
    required: true,
  },
});

import { watch, ref } from "vue";
import { RankingSortType } from "~/enum/v2/ranking-sort-type.ts";
const emit = defineEmits(["refreshTeam"]);

const totalProgress = ref(0);
const totalCo2Saving = ref(0);
const kickModalOpen = ref(false);
const kickTargetMemberId = ref(0);
const kickTargetMemberName = ref("");

watch(
  () => props.team,
  (newTeam) => {
    if (!Array.isArray(newTeam)) {
      totalProgress.value = 0;
      totalCo2Saving.value = 0;
      return;
    }

    totalProgress.value = newTeam.reduce((sum, item) => {
      return sum + (item?.progress ?? 0);
    }, 0);

    totalCo2Saving.value = newTeam.reduce((sum, item) => {
      return sum + (item?.co2Saving ?? 0);
    }, 0);
  },
  { immediate: true, deep: true }
);

const sortedTeam = computed(() =>
  [...props.team].sort((a, b) => b.progress - a.progress)
);

const isCurrentUserCaptain = computed(() => {
  return props.team.some(
    (member) => member.isCurrentUser && member.isTeamCaptain
  );
});

function onKickMember({
  memberId,
  memberName,
}: {
  memberId: number;
  memberName: string;
}) {
  kickTargetMemberId.value = memberId;
  kickTargetMemberName.value = memberName;
  kickModalOpen.value = true;
}

function onMemberKicked() {
  emit("refreshTeam");
}

function formatProgressValue(value: number): string {
  return formatCompetitiveChallengeProgress(value, props.challengeType, {
    roundUp: true,
  });
}
</script>
